import { useCallback, useEffect, useState } from 'react';
import { MdRefresh } from 'react-icons/md';
import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import * as S from './styles';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const notaLabel = { 5: 'Excelente', 4: 'Ótimo', 3: 'Regular', 2: 'Ruim', 1: 'Péssimo' };

const FILTERS = [
  { label: 'Todas', value: 0 },
  { label: '5 ★', value: 5 },
  { label: '4 ★', value: 4 },
  { label: '3 ★', value: 3 },
  { label: '2 ★', value: 2 },
  { label: '1 ★', value: 1 },
];

export default function Avaliacoes() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [filtro, setFiltro] = useState(0);

  const load = useCallback(async () => {
    if (!user?.IdEmpresa) return;
    setLoading(true);
    try {
      const [resumoRes, listaRes] = await Promise.all([
        api.get(`avaliacao/resumo/${user.IdEmpresa}`),
        api.get(`avaliacao/listarAdmin/${user.IdEmpresa}`),
      ]);
      setResumo(resumoRes.data);
      setAvaliacoes(listaRes.data);
    } finally {
      setLoading(false);
    }
  }, [user?.IdEmpresa]);

  useEffect(() => { load(); }, [load]);

  const listaFiltrada = filtro === 0
    ? avaliacoes
    : avaliacoes.filter((av) => av.Nota === filtro);

  if (loading) {
    return (
      <S.Container>
        <S.PageHeader>
          <S.PageTitle>Avaliações</S.PageTitle>
        </S.PageHeader>
        <LoadingSpinner />
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.PageHeader>
        <S.PageTitle>Avaliações dos Clientes</S.PageTitle>
        <S.RefreshBtn onClick={load}>
          <MdRefresh size={15} /> Atualizar
        </S.RefreshBtn>
      </S.PageHeader>

      {resumo && (
        <S.MetricsGrid>
          {/* Score geral */}
          <S.ScoreCard>
            <S.ScoreNumber>
              {resumo.media > 0 ? resumo.media.toFixed(1) : '—'}
            </S.ScoreNumber>
            <S.ScoreStars>
              {[1, 2, 3, 4, 5].map((s) => (
                <S.ScoreStar key={s} $filled={s <= Math.round(resumo.media)}>★</S.ScoreStar>
              ))}
            </S.ScoreStars>
            <S.ScoreLabel>
              {resumo.total > 0
                ? `${resumo.total} avaliação${resumo.total !== 1 ? 'ões' : ''}`
                : 'Nenhuma avaliação'}
            </S.ScoreLabel>
          </S.ScoreCard>

          {/* Distribuição por nota */}
          <S.DistCard>
            {[5, 4, 3, 2, 1].map((estrela) => {
              const qtd = resumo.distribuicao?.[estrela] ?? 0;
              const pct = resumo.total > 0 ? (qtd / resumo.total) * 100 : 0;
              return (
                <S.DistRow
                  key={estrela}
                  onClick={() => setFiltro((f) => (f === estrela ? 0 : estrela))}
                  title={`Filtrar por ${estrela} estrela${estrela > 1 ? 's' : ''}`}
                >
                  <S.DistStarLabel>{estrela}</S.DistStarLabel>
                  <S.DistStarIcon>★</S.DistStarIcon>
                  <S.BarTrack>
                    <S.BarFill $pct={pct} />
                  </S.BarTrack>
                  <S.DistCount>{qtd}</S.DistCount>
                </S.DistRow>
              );
            })}
          </S.DistCard>
        </S.MetricsGrid>
      )}

      {/* Filtros */}
      {avaliacoes.length > 0 && (
        <S.FilterRow>
          {FILTERS.map((f) => {
            const count = f.value === 0
              ? avaliacoes.length
              : avaliacoes.filter((av) => av.Nota === f.value).length;
            if (f.value !== 0 && count === 0) return null;
            return (
              <S.FilterChip
                key={f.value}
                $active={filtro === f.value}
                onClick={() => setFiltro(f.value)}
              >
                {f.label}
                <span style={{ opacity: 0.6, fontSize: 11 }}>({count})</span>
              </S.FilterChip>
            );
          })}
        </S.FilterRow>
      )}

      {/* Lista */}
      {listaFiltrada.length === 0 ? (
        <S.EmptyState>
          <S.EmptyIcon>⭐</S.EmptyIcon>
          <S.EmptyText>
            {filtro !== 0
              ? `Nenhuma avaliação de ${filtro} estrela${filtro > 1 ? 's' : ''}`
              : 'Nenhuma avaliação recebida ainda'}
          </S.EmptyText>
          <S.EmptySub>
            {filtro === 0
              ? 'As avaliações dos clientes aparecerão aqui após os pedidos concluídos.'
              : 'Tente outro filtro de nota.'}
          </S.EmptySub>
        </S.EmptyState>
      ) : (
        <>
          <S.SectionTitle>
            {filtro !== 0
              ? `${listaFiltrada.length} avaliação${listaFiltrada.length !== 1 ? 'ões' : ''} — ${filtro} estrela${filtro > 1 ? 's' : ''}`
              : `${listaFiltrada.length} avaliação${listaFiltrada.length !== 1 ? 'ões' : ''}`}
          </S.SectionTitle>
          <S.ReviewGrid>
            {listaFiltrada.map((av) => (
              <S.ReviewCard key={av.Id}>
                <S.CardTop>
                  <S.StarRow>
                    {[1, 2, 3, 4, 5].map((s) => (
                      <S.Star key={s} $filled={s <= av.Nota}>★</S.Star>
                    ))}
                  </S.StarRow>
                  <S.NotaBadge $nota={av.Nota}>{notaLabel[av.Nota]}</S.NotaBadge>
                </S.CardTop>

                {av.Comentario
                  ? <S.CommentText>{av.Comentario}</S.CommentText>
                  : <S.NoComment>Sem comentário</S.NoComment>
                }

                <S.CardFooter>
                  <S.ClientRow>
                    <S.ClientAvatar>
                      {(av.NomeCliente || 'C').charAt(0)}
                    </S.ClientAvatar>
                    <S.ClientName>{av.NomeCliente || 'Cliente'}</S.ClientName>
                  </S.ClientRow>
                  <S.DateText>{formatDate(av.CreatedAt)}</S.DateText>
                </S.CardFooter>
              </S.ReviewCard>
            ))}
          </S.ReviewGrid>
        </>
      )}
    </S.Container>
  );
}
